import { useMemo, useState } from 'react';
import { Search, X, Users, Check, ArrowUpDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ux/NativeSelect';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Candidate picker for scheduling.
 *
 * A dropdown works at ten candidates and fails at eighty, which is what a
 * public job link produces. Every candidate already carries an ATS score,
 * hiring tier and matched skills — a table puts that in front of the person
 * choosing, so the best fit is visible rather than buried behind a name.
 *
 * Candidates below the company pass threshold are muted rather than hidden:
 * they are still schedulable, just not the obvious choice.
 */

const TIERS = ['Strong Match', 'Good Match', 'Partial Match', 'Weak Match'];

const TIER_STYLES = {
    'Strong Match':  'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
    'Good Match':    'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/25',
    'Partial Match': 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
    'Weak Match':    'bg-muted text-muted-foreground border-border',
};

const SORTS = {
    ats:  { label: 'ATS score', fn: (a, b) => (b.atsScore ?? -1) - (a.atsScore ?? -1) },
    name: { label: 'Name',      fn: (a, b) => candidateName(a).localeCompare(candidateName(b)) },
};

/** Display name, falling back to the email local part when the resume had no name. */
function candidateName(c) {
    const full = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return full || (c.email || '').split('@')[0] || 'Unnamed';
}

/** One-line summary of where this candidate already is in the pipeline. */
function pipelineLabel(c) {
    const p = c.process;
    if (!p) return 'Not started';
    const status = String(p.status || '').replace(/_/g, ' ');
    return p.currentRound ? `${status} · round ${p.currentRound}` : status || 'In pipeline';
}

function scoreTone(score) {
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 60) return 'text-amber-600 dark:text-amber-400';
    return 'text-muted-foreground';
}

export default function CandidateTable({
    candidates = [],
    loading,
    value,
    onChange,
    passThreshold = 70,
    templateId,
}) {
    const [search, setSearch] = useState('');
    const [tier,   setTier]   = useState('');
    const [sortBy, setSortBy] = useState('ats');

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return candidates
            .filter(c => {
                if (tier && c.hiringTier !== tier) return false;
                if (!q) return true;
                return candidateName(c).toLowerCase().includes(q)
                    || (c.email || '').toLowerCase().includes(q);
            })
            .sort(SORTS[sortBy]?.fn || SORTS.ats.fn);
    }, [candidates, search, tier, sortBy]);

    if (loading) {
        return (
            <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-14 animate-pulse rounded-lg bg-muted/50" />
                ))}
            </div>
        );
    }

    // Nothing to choose from — say why, and where to go next.
    if (!candidates.length) {
        return (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 px-5 py-7 text-center">
                <Users className="h-8 w-8 text-muted-foreground/30" />
                <p className="text-sm font-medium text-foreground">No evaluated candidates yet</p>
                <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
                    Only candidates whose resume has been evaluated and accepted can be
                    scheduled. Evaluate resumes for this job description first.
                </p>
                <Button variant="outline" size="sm" className="mt-1" asChild>
                    <a href={`/admin/resumes${templateId ? `?templateId=${templateId}` : ''}`}>
                        Go to Resumes
                    </a>
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[11rem] flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search name or email…" className="h-8 pl-8 text-xs" />
                    {search && (
                        <button type="button" onClick={() => setSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <NativeSelect id="cand-tier" value={tier} onChange={e => setTier(e.target.value)}
                    className="w-[10rem] shrink-0 space-y-0 [&_label]:hidden [&_select]:h-8 [&_select]:text-xs [&_select]:py-1">
                    <option value="">All tiers</option>
                    {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                </NativeSelect>
                <NativeSelect id="cand-sort" value={sortBy} onChange={e => setSortBy(e.target.value)}
                    className="w-[9rem] shrink-0 space-y-0 [&_label]:hidden [&_select]:h-8 [&_select]:text-xs [&_select]:py-1">
                    {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>Sort: {s.label}</option>)}
                </NativeSelect>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                    {rows.length} of {candidates.length}
                </span>
            </div>

            {/* Rows */}
            <div className="max-h-[26rem] overflow-y-auto rounded-xl border border-border">
                <div className="sticky top-0 z-10 hidden grid-cols-12 gap-2 border-b border-border bg-muted/60 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur sm:grid">
                    <span className="col-span-4">Candidate</span>
                    <span className="col-span-2">ATS</span>
                    <span className="col-span-3">Skills matched</span>
                    <span className="col-span-3">Pipeline</span>
                </div>

                {rows.length === 0 ? (
                    <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                        No candidates match these filters.
                    </p>
                ) : rows.map(c => {
                    // Only accepted candidates have a user account to schedule against.
                    const id       = c.candidateId ? String(c.candidateId) : '';
                    const selected = value === id;
                    const below    = (c.atsScore ?? 0) < passThreshold;

                    return (
                        <button
                            key={id || c.resumeId}
                            type="button"
                            onClick={() => id && onChange(id)}
                            disabled={!id}
                            title={id ? undefined : 'Accept this resume first to create their account'}
                            aria-pressed={selected}
                            className={cn(
                                'grid w-full grid-cols-12 items-center gap-2 border-b border-border/60 px-3 py-2 text-left transition-colors last:border-0',
                                selected ? 'bg-primary/10' : 'hover:bg-muted/50',
                                below && !selected && 'opacity-70',
                                !id && 'cursor-not-allowed opacity-50',
                            )}
                        >
                            <div className="col-span-12 flex min-w-0 items-center gap-2 sm:col-span-4">
                                {selected
                                    ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                                    : <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-border" />}
                                <div className="min-w-0">
                                    <p className="truncate text-xs font-semibold text-foreground">{candidateName(c)}</p>
                                    <p className="truncate text-[11px] text-muted-foreground">{c.email}</p>
                                </div>
                            </div>

                            <div className="col-span-4 flex items-center gap-1.5 sm:col-span-2">
                                <span className={cn('text-xs font-bold tabular-nums', scoreTone(c.atsScore ?? 0))}>
                                    {c.atsScore ?? '—'}%
                                </span>
                                {c.hiringTier && (
                                    <span className={cn('hidden rounded border px-1 py-0.5 text-[9px] font-semibold lg:inline', TIER_STYLES[c.hiringTier])}>
                                        {c.hiringTier.replace(' Match', '')}
                                    </span>
                                )}
                            </div>

                            <div className="col-span-4 hidden min-w-0 sm:col-span-3 sm:block">
                                <p className="truncate text-[11px] text-muted-foreground">
                                    {c.matchedSkills?.length
                                        ? c.matchedSkills.slice(0, 3).join(', ') +
                                          (c.matchedSkills.length > 3 ? ` +${c.matchedSkills.length - 3}` : '')
                                        : '—'}
                                </p>
                            </div>

                            <div className="col-span-4 sm:col-span-3">
                                <p className="truncate text-[11px] capitalize text-muted-foreground">
                                    {pipelineLabel(c)}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>

            <p className="text-[11px] text-muted-foreground">
                Sorted best-fit first. Candidates below the {passThreshold}% pass threshold
                for this role are dimmed but can still be scheduled.
            </p>
        </div>
    );
}
